import React from 'react'
import styled from 'styled-components'
import { Box, TableColumn, Dejargonator, Link, Icon } from '@ally/metronome-ui'
import { muiHelpOutline } from '@ally/metronome-icons'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { Cell, Row } from 'react-table'
import {
  formatBalance,
  formatNoNegativeBalance,
  formatClosedDate,
  formatMaturityDate,
  formatPercentage,
} from '../../../utils'
import { AccountDetailsLink } from '../../Shared'
import { DashboardView } from '../../../CustomizeDashboard/types'

// table overrides default dejarg styling
const StyledDejarg = styled(Dejargonator)`
  text-align: left;
  white-space: normal;
`

const isNewAccount = (accountOpeningDate: string): boolean => {
  const conversion = 1000 * 60 * 60 * 24
  // Millisecond format
  const currentDate = new Date()
  const accountDate = new Date(accountOpeningDate)

  // Convert dates to UTC for accuracy
  const currentDateUTC = Date.UTC(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
  )
  const accountDateUTC = Date.UTC(
    accountDate.getFullYear(),
    accountDate.getMonth(),
    accountDate.getDate(),
  )

  // Sutract dates, then convert to days
  const daysOpen = Math.floor((currentDateUTC - accountDateUTC) / conversion)

  return daysOpen < 10
}

const GracePeriodDejarg = (): React.ReactElement => (
  <StyledDejarg
    trigger={{
      ariaLabel: '', // TODO: Need an aria label for this new prop
      variant: 'icon',
      content: <Icon icon={muiHelpOutline} size="md" fill="bluesuedeshoes" />,
    }}
    body="Because your CD matured, your new APY will be available the day after your grace period ends and is based on your term and balance tier at that time. Keep in mind, your CD includes the Ally Ten Day Best Rate Guarantee, so you'll get the best rate we offer for your term and balance tier if our rate goes up within 10 days of renewal."
    popoverAriaLabel="Because your CD matured, your new APY will be available the day after your grace period ends and is based on your term and balance tier at that time. Keep in mind, your CD includes the Ally Ten Day Best Rate Guarantee, so you'll get the best rate we offer for your term and balance tier if our rate goes up within 10 days of renewal."
  />
)

const UnFundedCdDejarg = (): React.ReactElement => (
  <StyledDejarg
    trigger={{
      ariaLabel: '', // TODO: Need an aria label for this new prop
      variant: 'icon',
      content: <Icon icon={muiHelpOutline} size="md" fill="bluesuedeshoes" />,
    }}
    heading="You haven't funded your account yet"
    body="Start earning interest by funding your account. When you fund your CD, you will receive the current rate we offer for your term and balance tier."
    popoverAriaLabel="You haven't funded your account yet. Start earning interest by funding your account. When you fund your CD, you will receive the current rate we offer for your term and balance tier."
  />
)

interface GetColumnsProps {
  view: DashboardView
  isLgDown: boolean
  hasStaleData: boolean
}

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface FooterProps {
  rows: Row<Account>[]
}

export const getColumns = ({
  view,
  isLgDown,
  hasStaleData,
}: GetColumnsProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown
  const columns = {
    total: {
      Header: 'CDs',
      Footer: 'Total',
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={cell.value}
              allytm="deposit-cdAccount-CDA"
              hasStaleData={hasStaleData}
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
    },
    currentBalance: {
      Header: 'Current',
      accessor: 'balance.current',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string => {
        const acctClosingDate = row.original.closeDate || ''

        if (!acctClosingDate && value === 0) {
          return (
            <Link
              to="/payments/transfers"
              text="Add money"
              withAllyTM="cds-add-money"
            />
          )
        }

        return acctClosingDate
          ? formatClosedDate(acctClosingDate)
          : formatBalance(value)
      },
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['balance.current'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return asCurrency(total)
      },
    },
    interestAccrued: {
      Header: 'Interest Accrued',
      accessor: 'interest.totalAccrued',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        row.original.closeDate ? null : formatNoNegativeBalance(value),
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['interest.totalAccrued'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return formatNoNegativeBalance(total)
      },
    },
    cdMaturityDate: {
      Header: 'Matures',
      accessor: 'meta.cdMaturityDate',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row } }: CellProps): React.ReactNode | string => {
        const { closeDate, openDate, balance, meta } = row.original
        // if they have a closed account leave blank
        if (closeDate) {
          return null
        }
        // if they have a balance or is a new account created within 10 days
        if (
          (balance?.current || isNewAccount(openDate ?? '')) &&
          meta?.cdMaturityDate
        ) {
          return formatMaturityDate(meta.cdMaturityDate)
        }
        return '--'
      },
    },
    apy: {
      Header: 'Annual Percentage Yield',
      accessor: 'interest.apy',
      alignment: 'right',
      isSortable: true,
      sortType: 'basic',
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string => {
        const { closeDate, indicator, balance, openDate } = row.original
        // if they have a closed account leave blank
        if (closeDate) {
          return null
        }
        // if they are within the grace period aka - InGracePeriod: true
        if (indicator?.cdInGracePeriod) {
          return (
            <>
              {formatPercentage(value)} <GracePeriodDejarg />
            </>
          )
        }
        // if they have no balance AND they are not a new account opened in last 10 days
        if (balance?.current === 0 && !isNewAccount(openDate ?? '')) {
          return (
            <>
              -- <UnFundedCdDejarg />
            </>
          )
        }
        // all remaining cases - InGracePeriod: false, they have a balance, and is not a new account created in last 10 days
        return formatPercentage(value)
      },
    },
    empty: {
      Header: '',
      accessor: 'empty',
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        null,
    },
  }

  const tileViewCols = [
    columns.total,
    columns.currentBalance,
    columns.cdMaturityDate,
    columns.empty,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.total,
    columns.currentBalance,
    columns.interestAccrued,
    columns.cdMaturityDate,
    columns.apy,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
